import type { Ref } from 'vue';

import {
    BackendErrorCode,
    useApiAcceptDirectSaleNegotiation,
    useApiMakeDirectSaleProposal,
    type BackendErrorEntity,
    type DirectSaleItemEntity,
    type ErrorType,
} from '~/apiClient';
import { NotificationDuration } from '~/types/notifications.type';

type UseDirectSaleNegotiationReturn = {
    makeProposal: (amount: number, negotiationId?: string) => Promise<string>;
    makeProposalIsPending: Ref<boolean>;
    accept: (negotiationId?: string) => Promise<void>;
    acceptIsPending: Ref<boolean>;
};

export default function useDirectSaleNegotiation(directSale: MaybeRefOrGetter<DirectSaleItemEntity>): UseDirectSaleNegotiationReturn {
    // COMPOSABLES
    const { tt, tn } = useTypedI18n();
    const { notifySuccess, notifyError } = useNotification();
    const { logError } = useLogs();
    const { data: authData } = useAuthUtils();
    const { mutateAsync: makeProposalMutateAsync, isPending: makeProposalIsPending } = useApiMakeDirectSaleProposal();
    const { mutateAsync: acceptMutateAsync, isPending: acceptIsPending } = useApiAcceptDirectSaleNegotiation();

    // DATA
    const isBuyer = computed(() => toValue(directSale).sellerCompanyId !== authData.value?.user?.companyId);

    // METHODS
    const notifyGenericApiError = (apiError: ErrorType<BackendErrorEntity>): never => {
        if (
            isBackendError(apiError.data) &&
            [BackendErrorCode.AuthTokenExpired, BackendErrorCode.AuthTokenNotValid].includes(apiError.data.error.code)
        ) {
            // Errors already handled elsewhere
        } else {
            notifyError({
                title: tt('directSaleNegotiation.notifications.apiError.title'),
                description: tt('directSaleNegotiation.notifications.apiError.description'),
                duration: NotificationDuration.NoTimeout,
            });
        }

        throw apiError;
    };

    const makeLogErrorHandler =
        (action: string) =>
        (error: ErrorType<BackendErrorEntity>, variables: Object): void => {
            logError('Error in DirectSale Negotiation', {
                action,
                directSaleId: toValue(directSale).id,
                userId: authData.value?.user?.id,
                apiError: error.data,
                apiData: variables,
            });
        };

    const makeProposal: UseDirectSaleNegotiationReturn['makeProposal'] = (amount, negotiationId) =>
        makeProposalMutateAsync(
            { directSaleId: toValue(directSale).id, data: { amount, negotiationId: !isBuyer.value ? negotiationId : undefined } },
            { onError: makeLogErrorHandler('makeProposal') }
        )
            .then(({ directSaleNegotiation }) => {
                if (directSaleNegotiation.outcome && directSaleNegotiation.closeAmount === amount) {
                    notifySuccess({
                        title: tt(
                            isBuyer.value
                                ? 'directSaleNegotiation.notifications.acceptByBuyer.title'
                                : 'directSaleNegotiation.notifications.acceptBySeller.title'
                        ),
                        description: tt(
                            isBuyer.value
                                ? 'directSaleNegotiation.notifications.acceptByBuyer.description'
                                : 'directSaleNegotiation.notifications.acceptBySeller.description',
                            { amount: tn(amount, 'currency') }
                        ),
                    });
                }

                return directSaleNegotiation.id;
            })
            .catch(apiError => {
                notifyGenericApiError(apiError);
                throw apiError;
            });

    const accept: UseDirectSaleNegotiationReturn['accept'] = negotiationId =>
        acceptMutateAsync(
            { directSaleId: toValue(directSale).id, data: { negotiationId: !isBuyer.value ? negotiationId : undefined } },
            { onError: makeLogErrorHandler('accept') }
        )
            .then(({ directSaleNegotiation }) => {
                notifySuccess({
                    title: tt(
                        isBuyer.value
                            ? 'directSaleNegotiation.notifications.acceptByBuyer.title'
                            : 'directSaleNegotiation.notifications.acceptBySeller.title'
                    ),
                    description: tt(
                        isBuyer.value
                            ? 'directSaleNegotiation.notifications.acceptByBuyer.description'
                            : 'directSaleNegotiation.notifications.acceptBySeller.description',
                        { amount: tn(directSaleNegotiation.closeAmount!, 'currency') }
                    ),
                });
            })
            .catch(notifyGenericApiError);

    return {
        makeProposal,
        makeProposalIsPending,
        accept,
        acceptIsPending,
    };
}
